import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatabase, type Driver } from '@/contexts/DatabaseContext';
import { useToast } from '@/contexts/ToastContext';
import DashboardLayout from '@/components/feature/DashboardLayout';

export default function Drivers() {
  const { t } = useTranslation('common');
  const { showToast } = useToast();
  const { drivers, addDriver, updateDriver, deleteDriver } = useDatabase();
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newDriver, setNewDriver] = useState({ name: '', contact: '' });
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [editValues, setEditValues] = useState({ name: '', contact: '' });

  const sortedDrivers = useMemo(() => {
    return [...drivers].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [drivers]);

  const filteredDrivers = useMemo(() => {
    if (!searchQuery) return sortedDrivers;
    const q = searchQuery.toLowerCase();
    return sortedDrivers.filter(
      (d) => d.name.toLowerCase().includes(q) || d.contact.includes(q)
    );
  }, [sortedDrivers, searchQuery]);

  const handleAddDriver = () => {
    if (!newDriver.name.trim()) return;

    const exists = drivers.some(
      (d) => d.name.toLowerCase() === newDriver.name.trim().toLowerCase()
    );
    if (exists) {
      showToast(t('driverExists'), 'error');
      return;
    }

    const driver: Driver = {
      id: `driver-${Date.now()}`,
      name: newDriver.name.trim(),
      contact: newDriver.contact.trim(),
    };

    addDriver(driver);
    setNewDriver({ name: '', contact: '' });
    setShowAddForm(false);
  };

  const handleDeleteDriver = (id: string) => {
    deleteDriver(id);
    setDeleteConfirm(null);
  };

  const startEditing = (driver: Driver) => {
    setEditingDriver(driver);
    setEditValues({ name: driver.name, contact: driver.contact });
  };

  const cancelEditing = () => {
    setEditingDriver(null);
    setEditValues({ name: '', contact: '' });
  };

  const saveEditing = () => {
    if (!editingDriver || !editValues.name.trim()) return;
    updateDriver(editingDriver.id, { name: editValues.name.trim(), contact: editValues.contact.trim() });
    setEditingDriver(null);
    setEditValues({ name: '', contact: '' });
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-[1200px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              {t('manageDrivers')}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">{drivers.length} {t('drivers').toLowerCase()}</p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-add-line" />
            {t('addNewDriver')}
          </button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-6 mb-6">
            <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-4">
              {t('addNewDriver')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('driverName')}</label>
                <input
                  type="text"
                  value={newDriver.name}
                  onChange={(e) => setNewDriver((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Nom du chauffeur"
                  className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('driverContact')}</label>
                <input
                  type="text"
                  value={newDriver.contact}
                  onChange={(e) => setNewDriver((prev) => ({ ...prev, contact: e.target.value }))}
                  placeholder="628 00 00 00"
                  className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleAddDriver}
                disabled={!newDriver.name.trim()}
                className="px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line mr-1.5" />
                {t('save')}
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-5 mb-4">
          <div className="relative max-w-md">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-foreground-400" />
            </div>
            <input
              type="text"
              placeholder={`${t('search')}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        </div>

        {/* Drivers list */}
        <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
          {filteredDrivers.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-user-star-line text-foreground-400 text-xl" />
              </div>
              <p className="text-sm text-foreground-500">{t('noDrivers')}</p>
            </div>
          ) : (
            <div className="divide-y divide-background-200">
              {filteredDrivers.map((driver) => {
                const isEditingThis = editingDriver?.id === driver.id;

                return (
                  <div key={driver.id}>
                    {isEditingThis ? (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 md:px-6 py-4 bg-background-100">
                        <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center shrink-0">
                          <span className="text-sm font-semibold text-secondary-700">
                            {editValues.name.charAt(0) || driver.name.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input
                            type="text"
                            value={editValues.name}
                            onChange={(e) => setEditValues((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder="Nom du chauffeur"
                            className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                          />
                          <input
                            type="text"
                            value={editValues.contact}
                            onChange={(e) => setEditValues((prev) => ({ ...prev, contact: e.target.value }))}
                            placeholder="628 00 00 00"
                            className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                          />
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            onClick={saveEditing}
                            disabled={!editValues.name.trim()}
                            className="px-3 py-1.5 bg-primary-500 text-background-50 rounded-lg text-xs font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                          >
                            {t('save')}
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="px-3 py-1.5 bg-background-200 text-foreground-700 rounded-lg text-xs font-medium hover:bg-background-300 transition-colors whitespace-nowrap"
                          >
                            {t('cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between px-4 md:px-6 py-4 hover:bg-background-100/50 transition-colors">
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center shrink-0">
                            <span className="text-sm font-semibold text-secondary-700">
                              {driver.name.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-900 truncate">
                              {driver.name}
                            </p>
                            <p className="text-xs text-foreground-500 mt-0.5">
                              {driver.contact || '-'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => startEditing(driver)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-400 hover:text-primary-600 transition-colors"
                            title={t('editDriver')}
                          >
                            <i className="ri-edit-line text-base" />
                          </button>

                          {deleteConfirm === driver.id ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleDeleteDriver(driver.id)}
                                className="px-3 py-1.5 bg-accent-500 text-background-50 rounded-lg text-xs font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
                              >
                                {t('delete')}
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-3 py-1.5 bg-background-100 text-foreground-600 rounded-lg text-xs font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
                              >
                                {t('cancel')}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(driver.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors"
                            >
                              <i className="ri-delete-bin-line text-base" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="px-4 py-3 bg-background-100 border-t border-background-200">
            <span className="text-xs text-foreground-500">
              {filteredDrivers.length} {t('drivers').toLowerCase()}
            </span>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}